import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../Customize_Css/Pagination.css';
import '../Customize_Css/ViewCustomers.css';
import { Dropdown, Modal, Button, Table, Card } from 'react-bootstrap';
import { FaFileExport, FaSearch, FaEye, FaUsers } from 'react-icons/fa';
import Sidebar from '../Components/Siderbar';
import Footer from '../Components/Footer';
import * as XLSX from 'xlsx';

const SORT_COLUMNS = [
    { key: 'CustomerId', label: 'Customer Id' },
    { key: 'CustomerName', label: 'Customer Name' },
    { key: 'PhoneNumber', label: 'Phone Number' },
    { key: 'food_type', label: 'Food Type' },
];

const foodTypeLabel = (value) => (value?.trim() ? value.trim() : 'Both');

const foodTypeClass = (value) => {
    const label = foodTypeLabel(value).toLowerCase();
    if (label.includes('non')) return 'is-nonveg';
    if (label === 'veg') return 'is-veg';
    return 'is-both';
};

const customerInitials = (name) => {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return 'C';
    return parts.slice(0, 2).map((part) => part[0].toUpperCase()).join('');
};

const ViewCustomers = () => {
    const BASE_URL = process.env.REACT_APP_BASE_URL;
    const epicxTenantId = localStorage.getItem('epicxTenantId');
    const AuthorizationToken = localStorage.getItem('token');
    const [data, setData] = useState([]);
    const [originalData, setOriginalData] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [selectedItems, setSelectedItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    useEffect(() => {
        const fetchCustomers = async () => {
            try {
                const response = await fetch(`${BASE_URL}/orders/customers/${epicxTenantId}/users`, {
                    headers: {
                        Authorization: `Bearer ${AuthorizationToken}`,
                    },
                });
                const result = await response.json();
                if (response.ok) {
                    const formattedData = result.map((user) => ({
                        CustomerId: user.userId,
                        CustomerName: user.name,
                        PhoneNumber: user.phoneNo,
                        EmailAddress: user.email,
                        Address: user.address.address || 'N/A',
                        houseNo: user.address.houseNo || 'N/A',
                        apartment: user.address.apartment || 'N/A',
                        accountStatus: user.accountStatus,
                        food_type: user.food_type
                    }));
                    setData(formattedData);
                    setOriginalData(formattedData);
                } else {
                    console.error('Failed to fetch customers:', result.message);
                }
            } catch (error) {
                console.error('Error fetching customers:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchCustomers();
    }, []);

    const handleSort = (key) => {
        let direction = sortConfig.direction === 'asc' ? 'desc' : 'asc';
        setSortConfig({ key, direction });
        const sortedData = [...data].sort((a, b) => {
            let aValue = a[key]?.toString().toLowerCase() || '';
            let bValue = b[key]?.toString().toLowerCase() || '';
            return direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
        });
        setData(sortedData);
    };

    const handleSearch = (value) => {
        setSearchTerm(value);
        setData(
            value
                ? originalData.filter((item) =>
                    Object.values(item).some((val) =>
                        (val ?? '').toString().toLowerCase().includes(value.toLowerCase())
                    )
                )
                : originalData
        );
        setCurrentPage(1);
    };

    const handleSelectAll = () => {
        setSelectedItems(selectedItems.length === data.length ? [] : data.map((item) => item.CustomerId));
    };

    const handleSelectItem = (CustomerId) => {
        setSelectedItems(
            selectedItems.includes(CustomerId)
                ? selectedItems.filter((id) => id !== CustomerId)
                : [...selectedItems, CustomerId]
        );
    };

    const handleExport = () => {
        const dataToExport = selectedItems.length > 0
            ? data.filter(item => selectedItems.includes(item.CustomerId))
            : data;

        const exportData = dataToExport.map(item => ({
            'Customer ID': item.CustomerId,
            'Customer Name': item.CustomerName,
            'Phone Number': item.PhoneNumber,
            'Email Address': item.EmailAddress,
            'Address': item.Address,
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Customers");
        XLSX.writeFile(wb, 'customers_data.xlsx');
    };

    const handleViewMore = (customerId) => {
        const customer = originalData.find(item => item.CustomerId === customerId);
        setSelectedCustomer(customer);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedCustomer(null);
    };

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentData = data.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.max(1, Math.ceil(data.length / itemsPerPage));
    const rangeStart = data.length === 0 ? 0 : indexOfFirstItem + 1;
    const rangeEnd = Math.min(indexOfLastItem, data.length);

    return (
        <>
            <Sidebar />
            <div className="customers-page">
                <header className="customers-hero">
                    <div>
                        <p className="customers-kicker">Customers</p>
                        <h1>Customer List</h1>
                        <p>Search, review, and export customer records without leaving this page.</p>
                    </div>
                </header>

                <section className="customers-toolbar" aria-label="Customer filters">
                    <p className="customers-toolbar-note">
                        {selectedItems.length > 0
                            ? <><strong>{selectedItems.length}</strong> selected for export</>
                            : 'Select rows to export a smaller list, or export everyone in the current results.'}
                    </p>
                    <div className="customers-toolbar-actions">
                        <div className="customers-search">
                            <FaSearch aria-hidden="true" />
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search customers..."
                                aria-label="Search customers"
                                value={searchTerm}
                                onChange={(e) => handleSearch(e.target.value)}
                            />
                        </div>
                        <Dropdown>
                            <Dropdown.Toggle className="customers-action-btn">
                                Actions
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={handleExport}>
                                    <FaFileExport className="me-2" /> Export
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </section>

                <Card className="customers-card">
                    <div className="customers-card-head">
                        <div>
                            <h2>All Customers</h2>
                            <p>{searchTerm ? `Results for “${searchTerm}”` : 'Showing every customer in this account'}</p>
                        </div>
                        <span className="customers-count">
                            <FaUsers aria-hidden="true" />
                            {data.length} {data.length === 1 ? 'customer' : 'customers'}
                        </span>
                    </div>
                    <Card.Body className="customers-card-body">
                        <Table responsive className="customers-table">
                            <thead>
                                <tr>
                                    <th className="customers-check-cell">
                                        <input
                                            className="customers-checkbox"
                                            type="checkbox"
                                            aria-label="Select all customers"
                                            checked={selectedItems.length === data.length && data.length > 0}
                                            onChange={handleSelectAll}
                                        />
                                    </th>
                                    {SORT_COLUMNS.map((column) => (
                                        <th key={column.key}>
                                            <button
                                                type="button"
                                                className="customers-sort"
                                                onClick={() => handleSort(column.key)}
                                            >
                                                {column.label}
                                                {sortConfig.key === column.key && (
                                                    <span className="customers-sort-mark" aria-hidden="true">
                                                        {sortConfig.direction === 'asc' ? '▲' : '▼'}
                                                    </span>
                                                )}
                                            </button>
                                        </th>
                                    ))}
                                    <th className="customers-action-cell">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan="6">
                                            <div className="customers-loading">
                                                <div className="spinner-border text-success" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                <h3>Loading customers</h3>
                                                <p>Fetching the latest customer records.</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : currentData.length > 0 ? (
                                    currentData.map((item) => (
                                        <tr
                                            key={item.CustomerId}
                                            className={selectedItems.includes(item.CustomerId) ? 'is-selected' : ''}
                                        >
                                            <td className="customers-check-cell">
                                                <input
                                                    className="customers-checkbox"
                                                    type="checkbox"
                                                    aria-label={`Select ${item.CustomerName}`}
                                                    checked={selectedItems.includes(item.CustomerId)}
                                                    onChange={() => handleSelectItem(item.CustomerId)}
                                                />
                                            </td>
                                            <td><span className="customers-id">{item.CustomerId}</span></td>
                                            <td>
                                                <div className="customers-person">
                                                    <span className="customers-avatar" aria-hidden="true">
                                                        {customerInitials(item.CustomerName)}
                                                    </span>
                                                    <strong>{item.CustomerName}</strong>
                                                </div>
                                            </td>
                                            <td>{item.PhoneNumber}</td>
                                            <td>
                                                <span className={`customers-food-badge ${foodTypeClass(item.food_type)}`}>
                                                    {foodTypeLabel(item.food_type)}
                                                </span>
                                            </td>
                                            <td className="customers-action-cell">
                                                <Button
                                                    className="customers-view-btn"
                                                    onClick={() => handleViewMore(item.CustomerId)}
                                                    title="View customer details"
                                                    aria-label={`View details for ${item.CustomerName}`}
                                                >
                                                    <FaEye aria-hidden="true" />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6">
                                            <div className="customers-empty">
                                                <span><FaUsers aria-hidden="true" /></span>
                                                <h3>No customers found</h3>
                                                <p>
                                                    {searchTerm
                                                        ? 'Try a different name, phone number, or customer id.'
                                                        : 'Customers will appear here once they are added.'}
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </Card.Body>
                    <div className="customers-card-footer">
                        <span className="customers-page-summary">
                            {data.length > 0
                                ? `Showing ${rangeStart}–${rangeEnd} of ${data.length}`
                                : 'Showing 0 customers'}
                        </span>
                        <div className="customers-pagination">
                            <nav>
                                <ul className="pagination mb-0" aria-label="Customers pagination">
                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link btn-sm"
                                            onClick={() => setCurrentPage(1)}
                                            aria-label="First page"
                                        >
                                            «
                                        </button>
                                    </li>
                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link btn-sm"
                                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                            aria-label="Previous page"
                                        >
                                            &lt;
                                        </button>
                                    </li>
                                    <li className="page-item active">
                                        <button className="page-link btn-sm" aria-current="page">
                                            {currentPage}
                                        </button>
                                    </li>
                                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link btn-sm"
                                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                            aria-label="Next page"
                                        >
                                            &gt;
                                        </button>
                                    </li>
                                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link btn-sm"
                                            onClick={() => setCurrentPage(totalPages)}
                                            aria-label="Last page"
                                        >
                                            »
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    </div>
                </Card>

                <div className="customers-footer">
                    <Footer />
                </div>

                <Modal
                    show={showModal && Boolean(selectedCustomer)}
                    onHide={handleCloseModal}
                    size="lg"
                    dialogClassName="customer-details-modal"
                    centered
                >
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <span className="customer-details-title-icon"><FaUsers aria-hidden="true" /></span>
                            <span>
                                <small>Customer</small>
                                Customer Details
                            </span>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {selectedCustomer && (
                            <>
                                <div className="customer-details-summary">
                                    <div>
                                        <h3>{selectedCustomer.CustomerName || 'Customer'}</h3>
                                        <p>{selectedCustomer.CustomerId}</p>
                                    </div>
                                    <span className="customer-status-badge">
                                        {selectedCustomer.accountStatus || 'N/A'}
                                    </span>
                                </div>
                                <div className="customer-details-grid">
                                    <div>
                                        <span>Phone Number</span>
                                        <strong>{selectedCustomer.PhoneNumber || 'N/A'}</strong>
                                    </div>
                                    <div>
                                        <span>Food Type</span>
                                        <strong>{foodTypeLabel(selectedCustomer.food_type)}</strong>
                                    </div>
                                    <div>
                                        <span>Email Address</span>
                                        <strong>{selectedCustomer.EmailAddress || 'N/A'}</strong>
                                    </div>
                                    <div>
                                        <span>Account Status</span>
                                        <strong>{selectedCustomer.accountStatus || 'N/A'}</strong>
                                    </div>
                                    <div>
                                        <span>House No</span>
                                        <strong>{selectedCustomer.houseNo}</strong>
                                    </div>
                                    <div>
                                        <span>Apartment</span>
                                        <strong>{selectedCustomer.apartment}</strong>
                                    </div>
                                    <div className="details-wide">
                                        <span>Address</span>
                                        <strong>{selectedCustomer.Address}</strong>
                                    </div>
                                </div>
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button className="customers-modal-close" onClick={handleCloseModal}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </>
    );
};

export default ViewCustomers;
